import { WellDetailsStates } from 'business/models/CustomModels';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { useEffect } from 'react';
import {
  IWellImageDTO,
  SaveImageRequestDTO,
  SaveImageTaskRequestDTO,
  SystemTaskResponseDTO,
  WellDetailsState,
} from 'business/models/Models';
import {
  getWellNumbersWithinRangeHorizontal,
  getWellNumbersWithinRangeVertically,
} from './utils';
import {
  useAllWellNavigationDetails,
  useLastSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { wellData } from './WellImage';
import { useRMDispatch } from 'redux/hooks';
import {
  getInspectionIdFromGroupByPlateId,
  GetNavigatorStatus,
  getNewId,
  GetWellsLabels,
  isObjectEmpty,
} from 'utils/Global';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import { useExperimentStates } from 'components/Experiment/hooks';
import { getRegionId as getWellRegionId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import SystemTaskService from 'business/services/systemTaskService';
import { LongRunningTask } from 'components/systemTasks/SystemTask';
import { addTask } from 'redux/reducers/systemTaskReducer';
import { SaveWellDropImageParams } from '../SaveImage/SaveImage';
import { getRegionIdCached, getWellImageParams } from 'components/well/utils';
import {
  useWellDropImageMetaData,
  useCurrentWellDropImageStatus,
  useShowOverrideTuning,
  useFLDropImageByKey,
} from 'redux/hooks/well';
import {
  getWellDropImageUniqueKey,
  getWellDropImageUniqueKeyByWellDetails,
} from 'redux/utils/well';
import WellDropImageStore from 'components/well/wellDropImageStore';
import { fetchWellDropImageBinaryThunk } from 'redux/thunks/well';
import {
  getTunedImageForDrop,
  getTuningWindowForDrop,
} from 'components/commandpanel/ImageTuning/ImageTuningManager';
import { setImageTuning } from 'redux/reducers/experimentReducer';
import { getTuningWindowForPlate } from 'components/commandpanel/ImageTuning/utils';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import store from 'redux/configureStore';
import { useFocusLevelImages } from 'components/well/hooks';
import { useFetchNavigatorImages } from 'components/commandpanel/Navigator/hooks';

export const getTunedImage = (
  exp,
  img,
  cpID,
  captureResultTuningValuesDto,
  onTuningCompletion,
) => {
  const tuningWindowVM = getTuningWindowForDrop(
    exp.experimentTuningValues,
    img,
    cpID,
    captureResultTuningValuesDto,
    false,
  );

  if (
    tuningWindowVM == undefined ||
    tuningWindowVM.lowerLevelLimit == undefined ||
    tuningWindowVM.upperLevelLimit == undefined
  ) {
    return img;
  }

  const tunedImage = getTunedImageForDrop(
    img,
    tuningWindowVM.isAutoLevelingOn,
    tuningWindowVM.autoLevelingLowerLimit,
    tuningWindowVM.autoLevelingUpperLimit,
    tuningWindowVM.lowerLevelLimit,
    tuningWindowVM.upperLevelLimit,
    onTuningCompletion,
    tuningWindowVM,
  );

  return tunedImage;
};

export const useGetWellImage = (
  exp: ExperimentDtoClient,
  params: WellDetailsStates,
  wellDetails: WellDetailsStates,
  setWellImage,
  setTunedImage,
  setBestFLImageTypeName,
  isVertical,
  verticalPrefetchingCount,
  horizontalPrefetchingCount,
  navigatorVerticalPrefetchingCount,
  navigatorHorizontalPrefetchingCount,
) => {
  const isNavActive = GetNavigatorStatus();
  const UIKey = getWellDropImageUniqueKeyByWellDetails(wellDetails); // current well image unique key
  const currentImageStatus = useCurrentWellDropImageStatus(UIKey); //  Should only fetch the data of current well, it'll avoid rerenders
  const wellDropMetaData = useWellDropImageMetaData();
  const plateDetails = exp.plateDto[0];
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const dropFLImage = useFLDropImageByKey(UIKey);
  const wellImagingParameterDetails = useLastSelectedDropImageParameters();
  const focusLevelImages = useFocusLevelImages(
    wellNavigationDetails,
    selectedDropImageParameters,
  );
  const dispatch = useRMDispatch();
  const fetchNavigatorImages = useFetchNavigatorImages(
    navigatorVerticalPrefetchingCount,
    navigatorHorizontalPrefetchingCount,
  );
  const totalWellsInExperiment =
    exp.plateDto.length * plateDetails.rows * plateDetails.cols;
  const showOverride = useShowOverrideTuning();

  /**
   * useEffect to calculate next images and fetch them
   */
  useEffect(() => {
    calculateNextImagesAndfetch();
  }, [UIKey]);

  useEffect(() => {
    if (isNavActive) {
      fetchNavigatorImages(
        exp,
        wellNavigationDetails,
        wellImagingParameterDetails,
        dispatch,
      );
    }
  }, [isNavActive]);

  /**
   * useEffect to set well image and tuned image when currentImageStatus changes
   */
  useEffect(() => {
    const isImageLoaded = currentImageStatus.fetchingStatus === EFetchingStatus.Loaded;

    const shouldUseFocusLevelImage =
      focusLevelImages?.length &&
      dropFLImage?.image &&
      focusLevelImages.some((x) => x.imageId === dropFLImage.imageId);

    const resolveTuningValues = (): ImageTuningValues | undefined => {
      const dropTuningApplied = !isObjectEmpty(
        currentImageStatus.meta?.updatedTuningValues,
      );

      if (dropTuningApplied) {
        return currentImageStatus.meta.updatedTuningValues;
      }

      if (!isImageLoaded) {
        // When not loaded, also try captureResultTuningValuesDto before falling back
        const captureResultTuning =
          wellDropMetaData?.[UIKey]?.captureResultTuningValuesDto;
        if (captureResultTuning) {
          return captureResultTuning;
        }
      }

      const tuningValues = exp.experimentTuningValues.find(
        (x) => x.captureProfileID === wellDropMetaData[UIKey]?.cpId,
      );

      const computedWindow = getTuningWindowForPlate(
        tuningValues,
        isImageLoaded ? original : null,
        wellDropMetaData[UIKey]?.cpId,
      );

      if (isImageLoaded) {
        dispatch(setImageTuning(computedWindow));
      }

      return computedWindow || undefined;
    };

    const applyFocusLevelImage = (tuning: ImageTuningValues = {}) => {
      const { lowerLevelLimit, upperLevelLimit, isAutoLevelingOn } = tuning;
      const img = new window.Image();
      img.src = dropFLImage.image;
      img.onload = () => {
        const tunedFLImage =
          lowerLevelLimit !== undefined && upperLevelLimit !== undefined
            ? getTunedImageForDrop(
                img,
                isAutoLevelingOn,
                null,
                null,
                lowerLevelLimit,
                upperLevelLimit,
              )
            : img;

        if (!isImageLoaded) {
          WellDropImageStore.set(UIKey, {
            original: img,
            tuned: tunedFLImage as HTMLImageElement,
            tuningValues: { lowerLevelLimit, upperLevelLimit, isAutoLevelingOn },
          });
          setWellImage(img);
        }

        setTunedImage({ image: tunedFLImage, imageStatus: EFetchingStatus.Loaded });
        setBestFLImageTypeName(dropFLImage.imageTitle);
      };
    };

    if (!isImageLoaded) {
      if (shouldUseFocusLevelImage) {
        applyFocusLevelImage(resolveTuningValues());
        return;
      }

      setTunedImage({
        image: null,
        imageStatus: currentImageStatus.fetchingStatus || EFetchingStatus.NotAvailable,
      });
      return;
    }

    const {
      original,
      tuned,
      tuningValues: tunedTuningValues,
    } = WellDropImageStore.get(UIKey) || {};
    setWellImage(original);

    const { lowerLevelLimit, upperLevelLimit, isAutoLevelingOn } =
      resolveTuningValues() || {};

    if (shouldUseFocusLevelImage) {
      applyFocusLevelImage({ lowerLevelLimit, upperLevelLimit, isAutoLevelingOn });
      return;
    }

    const shouldUpdateCache =
      tunedTuningValues?.lowerLevelLimit !== lowerLevelLimit ||
      tunedTuningValues?.upperLevelLimit !== upperLevelLimit ||
      tunedTuningValues?.isAutoLevelingOn !== isAutoLevelingOn;

    if (shouldUpdateCache) {
      const newTunedImage = getTunedImageForDrop(
        original,
        isAutoLevelingOn,
        null,
        null,
        lowerLevelLimit,
        upperLevelLimit,
      );

      if (showOverride) {
        WellDropImageStore.replaceTunedImage(UIKey, newTunedImage as HTMLImageElement, {
          lowerLevelLimit,
          upperLevelLimit,
          isAutoLevelingOn,
        });
      }

      setTunedImage({
        image: newTunedImage || original,
        imageStatus: EFetchingStatus.Loaded,
      });
      return;
    }

    // Use cached tuned image
    setTunedImage({ image: tuned || original, imageStatus: EFetchingStatus.Loaded });
  }, [currentImageStatus, showOverride, dropFLImage]);

  //todo: let's separate calculation of next images and fetching them
  const calculateNextImagesAndfetch = async () => {
    const { wellNum, plateID, regionName } = wellDetails;

    const wellNumbersVertical = getWellNumbersWithinRangeVertically(
      wellNum ?? params.wellNum,
      verticalPrefetchingCount >= 0 ? verticalPrefetchingCount * 2 : 7,
      plateDetails.cols,
      plateDetails.rows,
      [], //todo: don't worry about loaded images, just fetch next wellNumbers from current state.
      plateID,
      exp,
    );

    const wellNumbersHorizontal = getWellNumbersWithinRangeHorizontal(
      wellNum ?? params.wellNum,
      horizontalPrefetchingCount >= 0 ? horizontalPrefetchingCount * 2 : 7,
      1,
      plateDetails.rows * plateDetails.cols,
      [], //todo: don't worry about loaded images, just fetch next wellNumbers from current state.
      plateID,
      exp,
    );
    const wells: wellData[] = [...wellNumbersVertical, ...wellNumbersHorizontal].reduce(
      (accumulator, current) => {
        const existingWell = accumulator.find(
          (well) =>
            well.wellNum === current.wellNum &&
            well.plateNum === current.plateNum &&
            well.plateID === current.plateID,
        );
        if (!existingWell) {
          accumulator.push(current);
        }
        return accumulator;
      },
      [],
    );

    const processImage = async (well: wellData) => {
      const { plateID, wellNum } = well;
      const { dropNumber, cpID } = wellDetails;
      const regionID = getRegionIdCached(
        plateID,
        wellNum,
        dropNumber,
        regionName,
        exp.regions,
      );
      const imageKey = getWellDropImageUniqueKey(
        getWellImageParams({ ...wellDetails, plateID, wellNum, regionID }),
      );
      const _meta = wellDropMetaData?.[imageKey];
      const imageBatchId = getInspectionIdFromGroupByPlateId(wellDetails, plateID);

      if (!imageBatchId || !regionID) {
        return;
      }
      const imageStoreRef = store.getState().well.imageStore;
      if (!imageStoreRef[imageKey]) {
        dispatch(
          fetchWellDropImageBinaryThunk(
            {
              plateId: plateID || params.plateID,
              batchId: imageBatchId,
              wellNumber: wellNum || params.wellNum,
              dropNumber: dropNumber || params.dropNumber,
              cpId: cpID || params.cpID,
              regionId: regionID,
              regionName: regionName || params.regionName,
              bestFocusImageRequired: true,
              imageStoreId: _meta?.imageStoreId,
              bflImageTypeIndex: _meta?.bflImageTypeIndex,
              thumbnailImageType: _meta?.imageTypeShortName,
              imageTypeShortName: _meta?.imageTypeShortName,
            },
            imageKey,
            _meta,
            exp?.experimentTuningValues,
          ),
        );
      } else if (
        imageStoreRef[imageKey].fetchingStatus === EFetchingStatus.Loaded &&
        totalWellsInExperiment > WellDropImageStore.maxAllowedSize() - 10
      ) {
        WellDropImageStore.touch(imageKey);
      }
    };
    const promises = wells.map(processImage);
    await Promise.all(promises);
    if (isNavActive) {
      fetchNavigatorImages(
        exp,
        wellNavigationDetails,
        wellImagingParameterDetails,
        dispatch,
      );
    }
  };

  const key = getWellDropImageUniqueKey(getWellImageParams(wellDetails));
  return wellDropMetaData?.[key] || ({} as IWellImageDTO);
};

export const useSaveWellIDropImage = () => {
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const allWellDetails = useAllWellNavigationDetails();
  const { exp, dispatch } = useExperimentStates();

  const saveWellDropImage = (params: SaveWellDropImageParams) => {
    const {
      selectedCaptureProfileIds,
      saveAllDrops,
      saveAllRegions,
      saveAllInspections,
      saveAllFocusLevels,
      saveAllWellDropScores,
      saveAnnotations,
      saveScaleBar,
      saveCurrentZoom,
      imageZoomDto,
      saveIngredientInfo,
      onCompleteSaveImage,
      onErrorSaveImage,
    } = params;

    const { inspection, cpId, dropNumber, regionName } =
      selectedDropImageParameters || {};
    const plateId = allWellDetails?.[allWellDetails?.length - 1]?.plateID;
    const imageBatchId = inspection?.groupedInspections?.find(
      (gi) => gi.plateId === plateId,
    )?.imageBatchId;

    const wellDetails = allWellDetails.map((w) => {
      return {
        cpID: cpId,
        wellNum: w.wellNum,
        dropNumber: dropNumber,
        plateID: w.plateID,
        regionName: regionName,
        imageBatchId: imageBatchId,
        regionID: getWellRegionId(
          exp?.regions,
          w.wellNum,
          dropNumber,
          regionName,
          w.plateID,
        ),
        wellName: GetWellsLabels()[w.wellNum - 1].name,
      } as WellDetailsState;
    });

    const saveImageRequestDTO = {
      wellDetails: wellDetails,
      inspectionSetDTOs: exp.inspections,
      selectedCaptureProfileIds,
      saveAllDrops,
      saveAllRegions,
      saveAllInspections,
      saveAllFocusLevels,
      saveAllWellDropScores,
      saveAnnotations,
      saveScaleBar,
      saveCurrentZoom,
      saveIngredientInfo,
      imageZoomDto,
    } as SaveImageRequestDTO;

    const saveImageTaskRequestDTO = new SaveImageTaskRequestDTO();
    saveImageTaskRequestDTO.saveImageRequestDTO = saveImageRequestDTO;
    const task = {
      id: getNewId(),
      title: 'Saving Images',
      completionTitle: 'Image(s) Saved.',
      param: [saveImageTaskRequestDTO],
      serviceMathod: new SystemTaskService().saveImage,
      onComplete: (response: SystemTaskResponseDTO) => {
        onCompleteSaveImage(response);
      },
      onError: (response) => {
        onErrorSaveImage(response);
      },
    } as LongRunningTask;
    dispatch(addTask(task));
  };
  return { saveWellDropImage };
};
